import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { DetailAdditionalSection, Section } from "@/components/details";
import { agreementDetail, contractorDetail } from "@/schemas/agreement";
import { useAgreementData } from "../../hooks";
import TaxStatus from "@/components/TaxStatus";
import { Module, PATH_CONTRACTOR } from "@/constants";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { EntityListLink } from "@/components/EntityListLink";
import { useFormatters } from "@/lib/hooks/useFormatters";

export const AgreementDetailsSection = () => {
  const { t } = useTranslation();
  const { formatBoolean } = useFormatters();
  const { agreement, contractor } = useAgreementData();

  return (
    <CollapsibleSection title={t("agreements.details.title")}>
      <Section
        module={Module.Agreements}
        schema={agreementDetail}
        data={agreement}
      >
        <Section.Column>
          <Section.Field field="agreement_ref_number" />
          <Section.Field
            field="agreement_type_name"
            tooltip={t("agreements.fields.agreement_type_id_tooltip", {
              id: agreement.agreement_type_id,
            })}
          />
          <Section.Field
            field="agreement_company_name"
            tooltip={t("agreements.fields.company_id_tooltip", {
              id: agreement.agreement_company_id,
            })}
          />
          <Section.Field field="agreement_company_full_name" />
          <Section.Field
            field="agreement_status"
            value={t(`agreementStatuses.${agreement.agreement_status}`)}
          />
          <Section.Field field="is_b2b_agreement" />
          <Section.Field
            field="agreement_tax_status"
            value={
              <TaxStatus
                className="text-xs"
                value={agreement.agreement_tax_status}
              />
            }
          />
          <Section.Field field="recruitment_responsible_user_full_name" />
          <Section.Field field="hr_department_responsible_user_full_name" />
        </Section.Column>

        <Section.Column>
          <Section.Field field="agreement_start_date" />
          <Section.Field field="agreement_sign_date" />
          <Section.Field field="agreement_accepted_by_contractor_at" />
          <Section.Field field="agreement_statements_confirmed_by_contractor_at" />
          <Section.Field field="agreement_end_date" />
          <Section.Field field="agreement_last_amendment_number" />
          <Section.Field field="agreement_last_amendment_date" />
          <Section.Field
            field="agreement_statements_up_to_date"
            value={formatBoolean(
              agreement.agreement_statements_up_to_date,
              "agreements.fields.statements_up_to_date",
            )}
          />
        </Section.Column>
      </Section>

      <Separator className="my-4" />

      <DetailAdditionalSection
        title={t("contractors.details.title")}
        headerAction={
          <EntityListLink
            path={generatePath(PATH_CONTRACTOR, {
              contractorId: contractor.contractor_id,
            })}
            className="h-7 w-7 shrink-0"
          />
        }
      >
        <Section
          module={Module.Contractors}
          schema={contractorDetail}
          data={contractor}
        >
          <Section.Column>
            <Section.Field field="contractor_full_name" />
            <Section.Field field="contractor_email" />
          </Section.Column>
          <Section.Column>
            <Section.Field
              field="contractor_user_ref_number"
              tooltip={t("agreements.fields.contractor_id_tooltip", {
                id: contractor.contractor_id,
              })}
            />
            <Section.Field field="contractor_phone_number" />
          </Section.Column>
        </Section>
      </DetailAdditionalSection>
    </CollapsibleSection>
  );
};
